import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  isStructuredSession,
  LubavSessionType,
  SessionSwitchChoice,
} from '@backrs_ui/grow-ui/src/models/models';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';

import { addOfficeHoursMessage } from './coachLubav';
import { officeHoursApi } from './coachLubav.api';
import { initiateSession, resetOfficeHoursSession } from './coachLubav.thunk';
import {
  setActiveSessionType,
  clearSessionSwitch,
  setPendingStructuredChoice,
  setPreviewedSession,
} from '../lubavSessionState/lubavSessionState';
import { createSessionAction } from '../lubavSessionState/lubavSessionState.utils';
import { setSessionStatus } from '../sessionStatuses/sessionStatuses';
import { getTabKey } from '../tabs/tabKey';
import { setSelectedTabKey } from '../tabs/tabs.slice';

// Applies the user's session switch choice: switch into the target immediately, then for
// start/restart reset and initiate a fresh session; continue/discuss leave existing data in place.
export const applySessionSwitch = createAsyncThunk<
  undefined,
  {
    choice: SessionSwitchChoice;
    fromSession: LubavSessionType | null;
    fromSessionId?: number;
    choiceLabel: string;
  },
  { state: RootState }
>(
  'coachLubav/applySessionSwitch',
  async ({ choice, fromSession, fromSessionId, choiceLabel }, { dispatch }) => {
    const { targetSession, action } = choice;
    const startsFresh = action === 'start' || action === 'restart';

    if (fromSession) {
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          fromSession,
        )({ text: choiceLabel, sender: 'user' }),
      );

      if (fromSessionId) {
        dispatch(
          officeHoursApi.endpoints.appendMessage.initiate({
            session_id: fromSessionId,
            role: 'user',
            text: choiceLabel,
          }),
        );
      }
    }

    // Switch into the target before initiating; the target is now active, so the preview
    // that stood in for it is done.
    dispatch(setActiveSessionType(targetSession));
    dispatch(setPreviewedSession(null));
    // focus the session's FEATURED (output) tab
    dispatch(setSelectedTabKey(getTabKey({ kind: 'resource', boxType: 'FEATURED' })));
    dispatch(clearSessionSwitch());

    // continue/discuss resume an existing session — release the shell to load it now.
    if (!startsFresh) {
      dispatch(setPendingStructuredChoice(null));

      return undefined;
    }

    // start/restart begin a fresh session. Hold the shell's auto-init gate while we
    // initiate it ourselves so the shell can't start a second one.
    dispatch(setPendingStructuredChoice(targetSession));

    try {
      await dispatch(resetOfficeHoursSession({ activeSession: targetSession })).unwrap();
      await dispatch(
        initiateSession({ path: '', officeHoursId: targetSession, activeSession: targetSession }),
      ).unwrap();
    } catch (error) {
      console.error('applySessionSwitch: failed to initiate target session', error);
      // release the gate so the shell can recover the target session
      dispatch(setPendingStructuredChoice(null));
      throw error;
    }

    dispatch(setPendingStructuredChoice(null));

    // A freshly started/restarted session has no answers yet.
    if (isStructuredSession(targetSession)) {
      dispatch(
        setSessionStatus({
          sessionType: targetSession,
          entry: { status: 'none', sessionId: null },
        }),
      );
    }

    return undefined;
  },
);
